/**
 * IB-289 (FR-M7.6 UI) — a client-side, THIS-BROWSER-ONLY index of overlay ids the signed-in user has
 * created or opened, used solely to give `EligibilityOverlaysListScreen` something to render.
 *
 * <p><b>Why this exists (a disclosed backend gap, not a design choice).</b> The merged IB-288
 * controller exposes NO `GET /tenant-eligibility-overlays?base_model_id=` list/discovery endpoint —
 * only `create`/`get`/`approve` by id. Building a "your tenant's overlays" list therefore cannot be
 * backed by a real server list today (see the story report / `docs/design/
 * ib-289-tenant-eligibility-overlay-ui-design.md` §2.1 for the same gap flagged at design time).
 * This registry is an honest, clearly-labeled STAND-IN — "overlays you've created or opened in this
 * browser" — never presented as an authoritative, cross-device tenant list. Every row it lists is
 * still re-fetched LIVE via the real `GET /tenant-eligibility-overlays/{id}` before being shown, so
 * no fabricated data is ever displayed — only the SET of known ids is client-local.
 *
 * Follow-up filed: IB-288 should add the list endpoint so this registry can be retired in favor of
 * a real server-backed list (mirrors this story's own `[IB-289-follow-up]` note in the report).
 */

const KEY = 'iw.eligibilityOverlay.recentIds'
const MAX_ENTRIES = 25

function readIds(): string[] {
  try {
    const raw = window.sessionStorage.getItem(KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []
  } catch {
    return []
  }
}

export function recordOverlaySeen(id: string): void {
  try {
    const ids = readIds().filter((existing) => existing !== id)
    ids.unshift(id)
    window.sessionStorage.setItem(KEY, JSON.stringify(ids.slice(0, MAX_ENTRIES)))
  } catch {
    // Storage unavailable (private mode/quota) — the overlay itself already exists server-side;
    // this is a convenience index only, never a source of truth.
  }
}

export function listRecentOverlayIds(): string[] {
  return readIds()
}
